import { Checkbox, ColorPicker, InputNumber, Select, Slider, Spin, Switch } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Editor } from '@aitomol/core';
import { defaultIsovalue, type CubeGrid } from './types';
import { ISOSURFACE, addRepresentation, patchRepresentation, removeRepresentation, representationOf, supportsRepresentation } from './representations';

/**
 * Iso-surface controls for volumetric files.
 *
 * Replaces the JSmol `isosurface sign red blue <url>` script: the same red/blue
 * MO phase pair, drawn by the toolkit's own `isosurface` representation, but
 * with an editable isovalue, opacity, decimation, per-phase toggles and orbital
 * choice - none of which the script channel exposed.
 */

interface SurfaceSettings {
    isovalue: number;
    opacity: number;
    stride: number;
    showPositive: boolean;
    showNegative: boolean;
    showBox: boolean;
    positiveColor: string;
    negativeColor: string;
}

export interface IsosurfacePanelProps {
    editor: Editor;
    grid: CubeGrid | null;
    /** Index of the orbital currently decoded from a multi-orbital cube. */
    orbital: number;
    onOrbitalChange: (index: number) => void;
}

/** Coalesces slider drags so the surface is not rebuilt on every pixel. */
function useDebouncedPush(delay: number): (next: () => void) => void {
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(
        () => () => {
            if (timer.current) clearTimeout(timer.current);
        },
        []
    );
    return useMemo(
        () => (next: () => void) => {
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(next, delay);
        },
        [delay]
    );
}

export const IsosurfacePanel: React.FC<IsosurfacePanelProps> = ({ editor, grid, orbital, onOrbitalChange }) => {
    const { t } = useTranslation('common');
    const [enabled, setEnabled] = useState(true);
    const [settings, setSettings] = useState<SurfaceSettings>(() => ({
        isovalue: grid ? defaultIsovalue(grid) : 0.02,
        opacity: 0.85,
        stride: 1,
        showPositive: true,
        showNegative: true,
        showBox: false,
        positiveColor: '#e2564d',
        negativeColor: '#4d8fe2'
    }));
    const supported = supportsRepresentation(editor, ISOSURFACE);

    const debounced = useDebouncedPush(120);

    // A newly loaded cube turns the surface on automatically, which is what the
    // JSmol pane did by scripting `isosurface sign red blue`.
    useEffect(() => {
        if (!grid || !supported) return;
        const isovalue = defaultIsovalue(grid);
        setSettings((current) => ({ ...current, isovalue }));
        setEnabled(true);
        void addRepresentation(editor, { type: ISOSURFACE, select: 'all', params: { isovalue, opacity: 0.85, stride: 1 } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grid, supported]);

    // Removing the grid (another file was opened) drops the representation.
    useEffect(() => {
        if (grid) return;
        void removeRepresentation(editor, ISOSURFACE);
    }, [editor, grid]);

    const maxAbs = useMemo(() => {
        if (!grid) return 1;
        const value = Math.max(Math.abs(grid.minValue), Math.abs(grid.maxValue));
        return value > 0 ? value : 1;
    }, [grid]);

    const update = (next: Partial<SurfaceSettings>, immediate = false): void => {
        setSettings((current) => {
            const merged = { ...current, ...next };
            if (immediate) void patchRepresentation(editor, ISOSURFACE, { ...merged });
            else debounced(() => void patchRepresentation(editor, ISOSURFACE, { ...merged }));
            return merged;
        });
    };

    const onToggle = (checked: boolean): void => {
        setEnabled(checked);
        if (checked) void patchRepresentation(editor, ISOSURFACE, { ...settings });
        else void removeRepresentation(editor, ISOSURFACE);
    };

    if (!grid) return null;
    if (!supported) {
        return (
            <div className="aitomol-panel aitomol-panel-wide">
                <div className="aitomol-panel-title">{t('aitomol.isosurface')}</div>
                <div className="aitomol-panel-foot aitomol-warn">{t('aitomol.isosurface_unsupported')}</div>
            </div>
        );
    }

    return (
        <div className="aitomol-panel aitomol-panel-wide">
            <div className="aitomol-panel-title">
                <span>{t('aitomol.isosurface')}</span>
                <span className="aitomol-panel-meta">
                    {grid.nx}&times;{grid.ny}&times;{grid.nz}
                </span>
                <Switch size="small" checked={enabled && representationOf(editor, ISOSURFACE) !== undefined} onChange={onToggle} />
            </div>

            <div className="aitomol-row">
                <span className="aitomol-label">{t('aitomol.isovalue')}</span>
                <Slider
                    className="aitomol-slider"
                    min={maxAbs / 1000}
                    max={maxAbs}
                    step={maxAbs / 500}
                    value={Math.min(settings.isovalue, maxAbs)}
                    tooltip={{ formatter: (value) => value?.toExponential(2) }}
                    onChange={(value) => update({ isovalue: value })}
                />
                <InputNumber
                    size="small"
                    className="aitomol-number"
                    value={Number(settings.isovalue.toPrecision(3))}
                    step={maxAbs / 100}
                    min={0}
                    max={maxAbs}
                    onChange={(value) => update({ isovalue: Math.max(0, Number(value ?? 0)) })}
                />
            </div>

            <div className="aitomol-row">
                <span className="aitomol-label">{t('aitomol.opacity')}</span>
                <Slider
                    className="aitomol-slider"
                    min={0.05}
                    max={1}
                    step={0.05}
                    value={settings.opacity}
                    onChange={(value) => update({ opacity: value }, true)}
                />
                <span className="aitomol-value">{settings.opacity.toFixed(2)}</span>
            </div>

            <div className="aitomol-row">
                <span className="aitomol-label">{t('aitomol.resolution')}</span>
                <Select
                    size="small"
                    className="aitomol-select"
                    value={settings.stride}
                    onChange={(value) => update({ stride: value }, true)}
                    options={[
                        { value: 1, label: t('aitomol.resolution_full') },
                        { value: 2, label: '1/2' },
                        { value: 3, label: '1/3' },
                        { value: 4, label: '1/4' }
                    ]}
                />
            </div>

            <div className="aitomol-row aitomol-row-phases">
                <Checkbox checked={settings.showPositive} onChange={(event) => update({ showPositive: event.target.checked }, true)}>
                    <span className="aitomol-swatch" style={{ background: settings.positiveColor }} />+
                </Checkbox>
                <ColorPicker size="small" value={settings.positiveColor} onChange={(color) => update({ positiveColor: color.toHexString() }, true)} />
                <Checkbox checked={settings.showNegative} onChange={(event) => update({ showNegative: event.target.checked }, true)}>
                    <span className="aitomol-swatch" style={{ background: settings.negativeColor }} />&minus;
                </Checkbox>
                <ColorPicker size="small" value={settings.negativeColor} onChange={(color) => update({ negativeColor: color.toHexString() }, true)} />
                <Checkbox checked={settings.showBox} onChange={(event) => update({ showBox: event.target.checked }, true)}>
                    {t('aitomol.grid_box')}
                </Checkbox>
            </div>

            {grid.orbitalCount > 1 ? (
                <div className="aitomol-row">
                    <span className="aitomol-label">{t('aitomol.orbital')}</span>
                    <Select
                        size="small"
                        className="aitomol-select"
                        value={orbital}
                        onChange={(value) => onOrbitalChange(value)}
                        options={Array.from({ length: grid.orbitalCount }, (_, index) => ({
                            value: index,
                            label: grid.orbitalIndices[index] !== undefined ? `MO ${grid.orbitalIndices[index]}` : `#${index + 1}`
                        }))}
                    />
                </div>
            ) : null}

            <div className="aitomol-panel-foot">
                <span className="aitomol-hint">
                    {t('aitomol.range')} [{grid.minValue.toExponential(2)}, {grid.maxValue.toExponential(2)}]
                </span>
                <span className="aitomol-hint">
                    <Spin size="small" /> {t('aitomol.surface_hint')}
                </span>
            </div>
            {grid.title ? <div className="aitomol-panel-foot aitomol-hint">{grid.title}</div> : null}
        </div>
    );
};